import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getUserAddress, placeOrder, addAddress } from '../services/api';
import { formatPrice } from '../utils/formatPrice';
import { toast } from 'react-toastify';

const steps = ['Delivery Address', 'Payment Method', 'Confirm Order'];

const Payment = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const activeStep = 0;
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [addressDialog, setAddressDialog] = useState(false);
  const [addressForm, setAddressForm] = useState({ label: 'Home', address: '', city: '', state: '', pinCode: '' });

  const subtotal = getCartTotal();
  const deliveryCharge = subtotal >= 499 ? 0 : 40;
  const total = subtotal + deliveryCharge;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    const fetchAddress = async () => {
      try {
        const userId = localStorage.getItem('UserId');
        const response = await getUserAddress(userId);
        if (response.data.data && response.data.data[0]) {
          const userData = response.data.data[0];
          const userAddresses = userData.Addresses || [];
          setAddresses(userAddresses);
          // Pre-select default address
          const defaultAddr = userAddresses.find(a => a.isDefault);
          if (defaultAddr) {
            setSelectedAddressId(defaultAddr._id);
          } else if (userAddresses.length > 0) {
            setSelectedAddressId(userAddresses[0]._id);
          }
        }
      } catch (error) {
        console.error('Error fetching address:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddress();
  }, [isAuthenticated, cartItems.length, navigate]);

  const selectedAddress = addresses.find(a => a._id === selectedAddressId);

  const handleAddAddress = async () => {
    const userId = localStorage.getItem('UserId');
    try {
      const res = await addAddress(userId, addressForm);
      if (res.data.data) {
        setAddresses(res.data.data);
        // Select the newly added address
        const newAddr = res.data.data[res.data.data.length - 1];
        setSelectedAddressId(newAddr._id);
        toast.success('Address added');
      }
      setAddressDialog(false);
      setAddressForm({ label: 'Home', address: '', city: '', state: '', pinCode: '' });
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      toast.error('Please select a delivery address');
      return;
    }
    setPlacing(true);
    try {
      const deliveryAddress = {
        address: selectedAddress.address,
        city: selectedAddress.city,
        state: selectedAddress.state,
        pinCode: selectedAddress.pinCode,
      };
      await placeOrder({ items: cartItems, DeliveryAddress: deliveryAddress });
      clearCart();
      toast.success('Order placed successfully!');
      navigate('/orders');
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  if (loading) {
    return <Loader message="Loading checkout..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb
          items={[
            { label: 'Cart', path: '/cart' },
            { label: 'Checkout' },
          ]}
        />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          Checkout
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Grid container spacing={4}>
          {/* Left Side - Forms */}
          <Grid item xs={12} md={8}>
            {/* Delivery Address */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShippingIcon sx={{ color: '#8B4513' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Delivery Address
                  </Typography>
                </Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setAddressDialog(true)}
                  sx={{ color: '#8B4513' }}
                >
                  Add New
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {addresses.length > 0 ? (
                <RadioGroup
                  value={selectedAddressId}
                  onChange={(e) => setSelectedAddressId(e.target.value)}
                >
                  <Grid container spacing={2}>
                    {addresses.map((addr) => (
                      <Grid item xs={12} sm={6} key={addr._id}>
                        <FormControlLabel
                          value={addr._id}
                          control={<Radio sx={{ color: '#8B4513', '&.Mui-checked': { color: '#8B4513' } }} />}
                          label={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography fontWeight="bold">{addr.label}</Typography>
                                {addr.isDefault && <Chip label="Default" size="small" sx={{ bgcolor: '#8B4513', color: '#fff', height: 20, fontSize: '0.7rem' }} />}
                              </Box>
                              <Typography variant="body2" color="text.secondary">{addr.address}</Typography>
                              <Typography variant="body2" color="text.secondary">{addr.city}, {addr.state}</Typography>
                              <Typography variant="body2" color="text.secondary">Pin: {addr.pinCode}</Typography>
                            </Box>
                          }
                          sx={{
                            border: selectedAddressId === addr._id ? '2px solid #8B4513' : '1px solid #ddd',
                            borderRadius: 2,
                            p: 1.5,
                            mx: 0,
                            width: '100%',
                            bgcolor: selectedAddressId === addr._id ? '#faf8f5' : 'transparent',
                            alignItems: 'flex-start',
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No saved addresses. Please add a delivery address.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddressDialog(true)}
                    sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
                  >
                    Add Address
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Payment Method */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaymentIcon sx={{ color: '#8B4513' }} />
                <Typography variant="h6" fontWeight="bold">
                  Payment Method
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="cod"
                  control={<Radio sx={{ color: '#8B4513', '&.Mui-checked': { color: '#8B4513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">Cash on Delivery</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay when you receive your order
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'cod' ? '2px solid #8B4513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    mx: 0,
                    bgcolor: paymentMethod === 'cod' ? '#faf8f5' : 'transparent',
                  }}
                />
                <FormControlLabel
                  value="upi"
                  control={<Radio sx={{ color: '#8B4513', '&.Mui-checked': { color: '#8B4513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">UPI Payment</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay using Google Pay, PhonePe, Paytm
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'upi' ? '2px solid #8B4513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    mx: 0,
                    bgcolor: paymentMethod === 'upi' ? '#faf8f5' : 'transparent',
                  }}
                />
                <FormControlLabel
                  value="card"
                  control={<Radio sx={{ color: '#8B4513', '&.Mui-checked': { color: '#8B4513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">Credit/Debit Card</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visa, Mastercard, RuPay
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'card' ? '2px solid #8B4513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    mx: 0,
                    bgcolor: paymentMethod === 'card' ? '#faf8f5' : 'transparent',
                  }}
                />
              </RadioGroup>
            </Paper>
          </Grid>

          {/* Right Side - Order Summary */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#fff',
                position: 'sticky',
                top: 100,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Order Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Cart Items Preview */}
              <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                {cartItems.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <img
                      src={item.ProductMainImgUrl}
                      alt={item.ProductName}
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.ProductName.slice(0, 30)}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.ProductQuantity}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      Rs.{formatPrice(item.ProductPrice * item.ProductQuantity)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography fontWeight="bold">Rs.{formatPrice(subtotal)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Delivery</Typography>
                <Typography fontWeight="bold" color={deliveryCharge === 0 ? '#2e7d32' : 'inherit'}>
                  {deliveryCharge === 0 ? 'FREE' : `Rs.${deliveryCharge}`}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                  Rs.{formatPrice(total)}
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handlePlaceOrder}
                disabled={placing || !selectedAddress}
                startIcon={<CheckCircleIcon />}
                sx={{
                  py: 1.5,
                  bgcolor: '#2e7d32',
                  '&:hover': { bgcolor: '#1b5e20' },
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
              >
                {placing ? 'Placing Order...' : 'Place Order'}
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                By placing this order, you agree to our Terms & Conditions
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Add Address Dialog */}
      <Dialog open={addressDialog} onClose={() => setAddressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add New Address
          <IconButton onClick={() => setAddressDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Label (e.g. Home, Office)" value={addressForm.label} onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })} fullWidth size="small" />
            <TextField label="Address" value={addressForm.address} onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })} fullWidth size="small" />
            <TextField label="City" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} fullWidth size="small" />
            <TextField label="State" value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} fullWidth size="small" />
            <TextField label="Pin Code" value={addressForm.pinCode} onChange={(e) => setAddressForm({ ...addressForm, pinCode: e.target.value })} fullWidth size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAddress} sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}>
            Save Address
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Payment;
